import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const Welcome = () => {
  const navigate = useNavigate();
  const { id: memorialId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { signOut, isAuthenticated } = useAuth();
  const isB2BFlow = searchParams.get('b2b') === 'true';
  const [currentStep, setCurrentStep] = useState<'selection' | 'auth-choice'>('selection');
  const [selectedOption, setSelectedOption] = useState<"self" | "loved_one" | null>(null);
  

  const handleContinue = () => {
    if (!selectedOption) return;
    setCurrentStep('auth-choice');
  };

  const handleAuthChoice = async (type: 'login' | 'signup') => {
    // If user is logged in, log them out first
    if (isAuthenticated) {
      try {
        await signOut();
      } catch (error) {
        console.error('Error logging out:', error);
      }
    }
    
    // Navigate to auth page with appropriate parameters
    if (memorialId) {
      // NFC: Go to activation flow
      const param = type === 'login' ? 'login=true' : 'signup=true';
      const purposeParam = selectedOption ? `&memorialPurpose=${selectedOption}` : '';
      const b2bParam = isB2BFlow ? '&b2b=true' : '';
      navigate(`/auth?memorialId=${memorialId}&action=activate&${param}${purposeParam}${b2bParam}`);
    }
  };
  
  const handleBack = () => {
    setCurrentStep('selection');
  };

  const getEmpathyMessage = () => {
    if (selectedOption === "self") {
      return "You're giving a meaningful gift to those who'll carry your story forward. We'll help you capture it beautifully.";
    }
    if (selectedOption === "loved_one") {
      return "This is a beautiful way to remember them. We'll make it simple to bring their story to life.";
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-brand-background flex items-center justify-center p-3 sm:p-6">
      <div className="w-full max-w-2xl space-y-3 sm:space-y-5">
        {/* Logo */}
        <div className="text-center mb-3 sm:mb-6">
          <img 
            src="/lovable-uploads/pontis-logo.svg" 
            alt="Pontis" 
            className="h-10 sm:h-16 mx-auto mb-2 sm:mb-4"
          />
          <h1 className="font-serif text-xl sm:text-3xl font-bold text-brand-text-primary">We're glad you're here.</h1>
          <p className="text-brand-secondary text-xs sm:text-base mt-1 sm:mt-2">Let's create a space where their story — and their impact — will live on.</p>
        </div>

        {/* Step 1: Selection */}
        {currentStep === 'selection' && (
          <Card className="border-2 border-brand-primary/30 bg-white shadow-lg animate-in fade-in duration-300">
            <CardContent className="p-4 sm:p-8">
              <h2 className="font-serif text-lg sm:text-2xl font-semibold text-center text-brand-text-primary mb-4 sm:mb-8">
                Who will this memorial honor?
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-6">
                {/* Self Option */}
                <button
                  onClick={() => setSelectedOption("self")}
                  className={`p-3 sm:p-6 rounded-lg border-2 transition-all ${
                    selectedOption === "self"
                      ? "border-brand-primary bg-brand-primary/10 shadow-lg"
                      : "border-brand-primary/30 hover:border-brand-accent hover:bg-brand-primary/5"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2 sm:gap-3">
                    <div className={`p-2 sm:p-4 rounded-full transition-colors ${
                      selectedOption === "self" ? "bg-brand-primary text-brand-text-light" : "bg-brand-secondary/20 text-brand-text-primary"
                    }`}>
                      <User className="w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                    <h3 className="font-serif text-base sm:text-lg font-semibold text-brand-text-primary">Myself</h3>
                    <p className="text-xs sm:text-sm text-brand-secondary text-center">
                      I'm preserving my story for those I love.
                    </p>
                  </div>
                </button>

                {/* Loved One Option */}
                <button
                  onClick={() => setSelectedOption("loved_one")}
                  className={`p-3 sm:p-6 rounded-lg border-2 transition-all ${
                    selectedOption === "loved_one"
                      ? "border-brand-primary bg-brand-primary/10 shadow-lg"
                      : "border-brand-primary/30 hover:border-brand-accent hover:bg-brand-primary/5"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2 sm:gap-3">
                    <div className={`p-2 sm:p-4 rounded-full transition-colors ${
                      selectedOption === "loved_one" ? "bg-brand-primary text-brand-text-light" : "bg-brand-secondary/20 text-brand-text-primary"
                    }`}>
                      <Heart className="w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                    <h3 className="font-serif text-base sm:text-lg font-semibold text-brand-text-primary">A Loved One</h3>
                    <p className="text-xs sm:text-sm text-brand-secondary text-center">
                      I'm creating a tribute to someone who shaped my life.
                    </p>
                  </div>
                </button>
              </div>

              {/* Continue Button */}
              <Button
                onClick={handleContinue}
                disabled={!selectedOption}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-brand-text-light"
                size="lg"
              >
                Begin Creating
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Auth Choice */}
        {currentStep === 'auth-choice' && (
          <Card className="border-2 border-brand-primary/30 bg-white shadow-lg animate-in fade-in duration-300">
            <CardContent className="p-4 sm:p-8">
              {/* Empathy Message */}
              <div className="p-4 sm:p-6 bg-brand-accent/10 rounded-lg mb-6 sm:mb-8">
                <p className="text-center text-sm sm:text-base text-brand-text-primary italic">
                  {getEmpathyMessage()}
                </p>
              </div>

              <h2 className="font-serif text-lg sm:text-2xl font-semibold text-center text-brand-text-primary mb-6 sm:mb-8">
                Let's get you started
              </h2>

              <div className="space-y-3 sm:space-y-4">
                {/* Sign Up Button */}
                <Button
                  onClick={() => handleAuthChoice('signup')}
                  className="w-full bg-brand-primary hover:bg-brand-primary/90 text-brand-text-light"
                  size="lg"
                >
                  Create an Account
                </Button>

                {/* Log In Button */}
                <Button
                  onClick={() => handleAuthChoice('login')}
                  variant="outline"
                  className="w-full border-2 border-brand-primary/30 hover:border-brand-accent hover:bg-brand-primary/5 text-brand-text-primary"
                  size="lg"
                >
                  I Already Have an Account
                </Button>

                {/* Back Link */}
                <button
                  onClick={handleBack}
                  className="w-full text-sm text-brand-secondary hover:text-brand-accent transition-colors pt-2"
                >
                  ← Go back
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Support Text */}
        <p className="text-center text-xs sm:text-sm text-brand-secondary">
          We're always here if you need help —{" "}
          <a href="mailto:support@pontis.life" className="text-brand-accent hover:underline">
            support@pontis.life
          </a>
        </p>
      </div>
    </div>
  );
};

export default Welcome;
